import { useMemo } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Badge,
  DataTable,
  EmptyState,
  PageBody,
  SectionLabel,
  type DataTableColumn,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { SkillRecommendationStatsHeader } from '@/components/admin/recommendation-runs/SkillRecommendationStatsHeader.tsx';
import {
  formatRunTime,
  runStatusTone,
} from '@/components/admin/recommendation-runs/runStatus.ts';
import {
  fetchRecommendationRuns,
  type RecommendationRunSummary,
} from '@/lib/skillRecommendations.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

interface RunHistorySearch {
  /** One-based, as the query string carries it. */
  page: number;
}

export const Route = createFileRoute('/_app/admin/recommendation-runs/')({
  staticData: { orgScope: 'none' },
  validateSearch: (search: Record<string, unknown>): RunHistorySearch => {
    const page = Number(search.page);
    return { page: Number.isFinite(page) && page > 0 ? page : 1 };
  },
  component: RunHistoryRoute,
});

function RunHistoryRoute() {
  const { page } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <RunHistoryPage
      page={page}
      onPageChange={(next) => void navigate({ search: { page: next } })}
    />
  );
}

export interface RunHistoryPageProps {
  /** One-based, held by the route's search params. */
  page: number;
  onPageChange: (page: number) => void;
}

/**
 * Everything the daily recommendation engine has produced.
 *
 * One row is one user's outreach for one run — the skill that won. Opening it
 * shows the whole scoring log behind that choice.
 */
export function RunHistoryPage({ page, onPageChange }: RunHistoryPageProps) {
  const router = useRouter();

  const runs = useQuery({
    queryKey: ['recommendationRuns', page],
    queryFn: () => fetchRecommendationRuns(page, PAGE_SIZE),
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const rows = useMemo(() => runs.data?.data ?? [], [runs.data]);
  const total = runs.data?.meta.totalItems ?? 0;

  const columns = useMemo<DataTableColumn<RecommendationRunSummary>[]>(
    () => [
      {
        key: 'recommended_at',
        header: 'When',
        width: 'w-44 max-mobile:w-auto',
        render: (run) => (
          <span className='whitespace-nowrap text-ink-secondary'>
            {formatRunTime(run.recommended_at)}
          </span>
        ),
      },
      {
        key: 'user_name',
        header: 'User',
        hideBelow: 'mobile',
        render: (run) => run.user_name ?? '—',
      },
      {
        key: 'skill_name',
        header: 'Skill',
        render: (run) => <span className='font-semibold text-ink'>{run.skill_name}</span>,
      },
      {
        key: 'final_score',
        header: 'Score',
        hideBelow: 'mobile',
        width: 'w-20',
        render: (run) => (
          <span className='font-mono text-ink-secondary'>
            {run.final_score.toFixed(1)}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 'w-32',
        render: (run) => <Badge tone={runStatusTone(run.status)}>{run.status}</Badge>,
      },
      {
        /* No width: the reason is the long one, so it takes what is left. */
        key: 'reason',
        header: 'Reason',
        hideBelow: 'mobile',
        render: (run) => (
          <span className='line-clamp-2 text-ink-secondary'>
            {run.skipped_reason ?? run.reason}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader title='Recommendation runs' />
      <PageBody className='flex flex-col gap-5'>
        <SkillRecommendationStatsHeader />

        <div>
          <SectionLabel as='h2'>Runs</SectionLabel>
          <p className='mt-1 text-sm text-ink-muted'>
            Outreaches produced by the daily recommendation engine. Each row is the one
            skill the engine picked for a user; its status tracks the outreach lifecycle
            (pending → delivered / skipped / superseded). Open a row to see the full
            scoring log for that run — the winner plus every losing candidate.
          </p>
        </div>

        <DataTable<RecommendationRunSummary>
          label='Recommendation runs'
          columns={columns}
          rows={rows}
          rowKey={(run) => run.id}
          loading={runs.isFetching}
          pagination={{ page, pageSize: PAGE_SIZE, total }}
          onPageChange={onPageChange}
          rowHref={(run) =>
            `/admin/recommendation-runs/${run.run_id}/users/${run.user_id}`
          }
          onNavigate={(href) => void router.navigate({ href })}
          emptyState={<EmptyState title='No recommendation runs yet' />}
        />
      </PageBody>
    </>
  );
}
